import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import LaunchLegalHeader from "../Components/common/LaunchLegalHeader";

const privacySections = [
  {
    id: "information-we-collect",
    title: "1. Information We Collect",
    intro: "We may collect the following:",
    items: [
      "Personal Information: Name, phone number, address",
      "Health Information: Prescriptions and medicine details",
      "Device Information: Device type, IP address, app usage",
      "Location Data: For delivery and service availability",
    ],
  },
  {
    id: "how-we-use",
    title: "2. How We Use Your Information",
    intro: "We use your information to:",
    items: [
      "Process and deliver orders",
      "Verify prescriptions",
      "Provide customer support",
      "Improve our services",
      "Send updates and offers (with consent)",
      "Prevent fraud and misuse",
    ],
  },
  {
    id: "sharing",
    title: "3. Sharing of Information",
    intro: "We do not sell your data. We may share information with:",
    items: [
      "Delivery partners",
      "Licensed pharmacists",
      "Payment gateways",
      "Legal authorities (if required)",
    ],
  },
  {
    id: "data-security",
    title: "4. Data Security",
    items: [
      "We implement appropriate security measures to protect your data.",
      "However, no system is completely secure, and we cannot guarantee absolute security.",
      "You are responsible for safeguarding your account credentials.",
    ],
  },
  {
    id: "your-rights",
    title: "5. Your Rights",
    intro: "You may:",
    items: [
      "Access or update your data",
      "Request deletion of your account",
      "Opt-out of marketing communications",
    ],
  },
  {
    id: "data-retention",
    title: "6. Data Retention",
    content: [
      "We retain your data only as long as necessary for service delivery or legal compliance.",
    ],
  },
  {
    id: "childrens-privacy",
    title: "7. Children's Privacy",
    content: [
      "This Platform is not intended for individuals below 18 years of age.",
    ],
  },
  {
    id: "changes",
    title: "8. Changes to Privacy Policy",
    content: [
      "We may update this policy periodically. Continued use of the Platform indicates acceptance.",
    ],
  },
  {
    id: "contact-us",
    title: "9. Contact Us",
    isContact: true,
  },
  {
    id: "data-deletion",
    title: "10. Data Deletion Requests",
    content: [
      "You may request deletion of your data or account by contacting us via email. We will process such requests within a reasonable timeframe, subject to legal obligations.",
    ],
  },
];

const PrivacyPolicy = ({ launchOnly = false }) => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [activeSection, setActiveSection] = useState(null);

  return (
    <div className={`min-h-screen bg-[#f8fafa] font-poppins ${launchOnly ? "" : "mt-36 lg:mt-0"}`}>
      {launchOnly && <LaunchLegalHeader />}
      <Helmet>
        <title>Privacy Policy | MediSYS Pharmacy</title>
        <meta name="description" content="Read MediSYS Pharmacy's Privacy Policy to understand how we collect, use, and protect your information." />
        <meta name="keywords"    content="privacy policy, MediSYS, data protection, personal information" />
        <meta name="robots"      content="index, follow" />
        <meta property="og:title"       content="Privacy Policy | MediSYS Pharmacy" />
        <meta property="og:description" content="How MediSYS Pharmacy collects, uses, and protects your data." />
      </Helmet>

      {/* ── Hero strip ── */}
      <div className={`bg-[#162c2a] py-10 relative overflow-hidden ${launchOnly ? "" : "px-5 sm:px-8 lg:px-14"}`}>
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect x='17' y='10' width='6' height='20' rx='2' fill='white'/%3E%3Crect x='10' y='17' width='20' height='6' rx='2' fill='white'/%3E%3C/svg%3E")`,
            backgroundRepeat: "repeat",
            backgroundSize: "40px 40px",
          }}
        />
        <div className={launchOnly ? "relative launch-wrap" : "relative"}>
          <div className="flex items-center gap-3 mb-3">
            <span className="w-6 h-[2px] bg-[#ff601e] rounded-full" />
            <span className="text-[11px] font-semibold uppercase tracking-widest text-[#5fbdb3]">Legal</span>
          </div>
          <h1 className="font-bold text-[28px] sm:text-[36px] text-white leading-tight">
            Privacy Policy
          </h1>
          <p className="mt-2 text-[13px] text-white/50 max-w-[500px]">
            This Privacy Policy explains how we collect, use, and protect your information.
          </p>
          <div className="flex items-center gap-2 mt-4">
            <span className="text-[11px] text-white/35">Last updated:</span>
            <span className="text-[11px] font-medium text-white/60">1st September, 2026</span>
          </div>
        </div>
      </div>

      <div className={launchOnly ? "launch-wrap py-10" : "mx-auto px-4 sm:px-6 lg:px-8 py-10"}>

        {/* ── Intro block ── */}
        <div className="bg-white rounded-2xl border border-[#e2eaea] shadow-[0_2px_12px_rgba(22,44,42,0.07)] p-6 sm:p-8 mb-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-8 h-8 rounded-lg bg-[#eaf4f3] border border-[#d4ecea] flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1e4a46" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
            </div>
            <h2 className="font-bold text-[17px] text-[#1e4a46]">Your Privacy Matters</h2>
          </div>
          <p className="text-[13px] sm:text-[14px] text-[#374747] leading-[1.85] text-justify">
            <span className="font-semibold text-[#1e4a46]">MediSYS Pharmacy</span> values your privacy. This Privacy Policy explains how we collect, use, and protect your information.
          </p>
        </div>

        {/* ── Sections ── */}
        <div className="flex flex-col gap-3">
          {privacySections.map((section, idx) => {
            const isOpen = activeSection === idx;
            return (
              <div
                key={section.id}
                className={`bg-white rounded-2xl border transition-all duration-200 overflow-hidden ${
                  isOpen
                    ? "border-[#5fbdb3] shadow-[0_4px_18px_rgba(22,44,42,0.1)]"
                    : "border-[#e2eaea] shadow-[0_2px_8px_rgba(22,44,42,0.05)] hover:border-[#c5d5d4]"
                }`}
              >
                <button
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
                  onClick={() => setActiveSection(isOpen ? null : idx)}
                >
                  <div className="flex items-center gap-3">
                    <span className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-[11px] font-bold transition-colors duration-200 ${
                      isOpen ? "bg-[#1e4a46] text-white" : "bg-[#eaf4f3] text-[#1e4a46]"
                    }`}>
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <h3 className={`text-[13px] sm:text-[14px] font-semibold transition-colors duration-200 ${
                      isOpen ? "text-[#1e4a46]" : "text-[#374747]"
                    }`}>
                      {section.title}
                    </h3>
                  </div>
                  <span className={`flex-shrink-0 w-7 h-7 rounded-xl flex items-center justify-center transition-all duration-200 ${
                    isOpen ? "bg-[#1e4a46] text-white rotate-180" : "bg-[#f1f5f5] text-[#8faba9]"
                  }`}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M6 9l6 6 6-6"/>
                    </svg>
                  </span>
                </button>

                {isOpen && (
                  <div className="px-5 pb-5 border-t border-[#f1f5f5] pt-4">
                    <div className="pl-10 flex flex-col gap-3">

                      {/* Regular paragraphs */}
                      {section.content?.map((para, i) => (
                        <div key={i} className="flex gap-3">
                          <span className="flex-shrink-0 w-1 self-stretch bg-[#ff601e] rounded-full opacity-60" />
                          <p className="text-[13px] sm:text-[14px] text-[#374747] leading-[1.85] text-justify">{para}</p>
                        </div>
                      ))}

                      {/* Optional intro line before a bullet list */}
                      {section.intro && (
                        <p className="text-[13px] sm:text-[14px] text-[#374747] leading-[1.85]">{section.intro}</p>
                      )}

                      {/* Bullet list */}
                      {section.items && (
                        <ul className="flex flex-col gap-2">
                          {section.items.map((item, i) => (
                            <li key={i} className="flex items-start gap-2.5 text-[13px] sm:text-[14px] text-[#374747] leading-relaxed">
                              <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-[#5fbdb3] mt-2" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      )}

                      {/* Contact section */}
                      {section.isContact && (
                        <div className="bg-[#eaf4f3] border border-[#d4ecea] rounded-xl p-4 flex flex-col gap-2">
                          <p className="text-[13px] text-[#374747] mb-1">
                            For privacy-related queries:
                          </p>
                          {[
                            { label: "Name",  value: "MediSYS Pharmacy" },
                            { label: "Email", value: "medisys.customercare@gmail.com" },
                          ].map((row) => (
                            <div key={row.label} className="flex items-start gap-2 text-[13px]">
                              <span className="font-semibold text-[#1e4a46] min-w-[60px] flex-shrink-0">{row.label}:</span>
                              <span className="text-[#374747]">{row.value}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* ── Bottom CTA ── */}
        <div className="mt-8 bg-[#162c2a] rounded-2xl p-6 relative overflow-hidden">
          <div
            className="absolute inset-0 opacity-[0.04] pointer-events-none"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect x='17' y='10' width='6' height='20' rx='2' fill='white'/%3E%3Crect x='10' y='17' width='20' height='6' rx='2' fill='white'/%3E%3C/svg%3E")`,
              backgroundRepeat: "repeat",
              backgroundSize: "40px 40px",
            }}
          />
          <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <p className="font-bold text-[15px] sm:text-[17px] text-white mb-1">Questions about your privacy?</p>
              <p className="text-[12px] text-white/50">Reach out to our support team — we're here to help.</p>
            </div>
            <a
              href={launchOnly ? "mailto:medisys.customercare@gmail.com" : "/contact-us"}
              className="flex-shrink-0 flex items-center gap-2 bg-[#ff601e] hover:bg-[#e55519] text-white font-semibold text-[13px] px-5 h-10 rounded-xl transition-all duration-200 shadow-[0_3px_12px_rgba(255,96,30,0.35)] hover:-translate-y-0.5 whitespace-nowrap"
            >
              Contact Us
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
